import React, { useState, useEffect } from "react";
import Context from "../../Context";
import { useToast } from "../../components/ui";
import { getCustomerUrl } from "../../constants";
import { Header } from "../../components/modules";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { FaPlus, FaCopy, FaEye, FaToggleOn, FaToggleOff, FaFileExcel } from "react-icons/fa";
import { IoStatsChart } from "react-icons/io5";

import { FiLink } from "react-icons/fi";
import { Spinner } from "reactstrap";
import {
  exportTrackingLinkToXLSX,
  exportAllTrackingLinksToXLSX,
} from "./exportTrackingLink";

const inputCls =
  "box-border border w-full border-gray-300 py-2 px-3 font-semibold rounded-md text-sm focus:outline-none focus:border-navy";

export const ManageTrackingLinks = () => {
  const { callApi, navigate, configs } = Context();
  const { toast } = useToast();

  const [trackingLinks, setTrackingLinks] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloadingCode, setDownloadingCode] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    contact: "",
    note: "",
  });

  useEffect(() => {
    fetchTrackingLinks();
  }, []);

  const fetchTrackingLinks = async () => {
    setLoading(true);
    const result = await callApi(
      "trackingLinks-getAllTrackingLinkStats",
      { roundDate: configs?.roundDate },
      { notShowLoading: true }
    );
    if (result && result.status) {
      setTrackingLinks(result.data || []);
    } else {
      setTrackingLinks([]);
    }
    setLoading(false);
  };

  const handleOpenCreateModal = async () => {
    setEditingItem(null);
    const res = await callApi("trackingLinks-generateCode", {}, { notShowLoading: true });
    setFormData({
      name: "",
      code: res?.code || "",
      contact: "",
      note: "",
    });
    setShowModal(true);
  };

  const handleExportAllXLSX = () => {
    try {
      const filename = exportAllTrackingLinksToXLSX({
        trackingLinks: trackingLinks || [],
        selectedRound: configs?.roundDate,
      });
      toast({
        className: "bg-[rgb(59,176,101)]",
        title: "ดาวน์โหลดรายงานภาพรวมสำเร็จ",
        description: filename,
      });
    } catch (error) {
      console.error("Export all error:", error);
      toast({
        className: "bg-[#c15b78]",
        title: "ดาวน์โหลดรายงานไม่สำเร็จ",
        description: error.message,
      });
    }
  };

  const handleOpenEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || "",
      code: item.code || "",
      contact: item.contact || "",
      note: item.note || "",
    });
    setShowModal(true);
  };

  const handleDownloadXLSX = async (item) => {
    try {
      setDownloadingCode(item.code);
      const [statsRes, eventsRes] = await Promise.all([
        callApi(
          "trackingLinks-getTrackingLinkStats",
          { code: item.code, roundDate: configs?.roundDate },
          { notShowLoading: true }
        ),
        callApi(
          "trackingLinks-getTrackingLinkEvents",
          { code: item.code, limit: 500, roundDate: configs?.roundDate },
          { notShowLoading: true }
        ),
      ]);

      const events = eventsRes?.data || [];
      const stats = statsRes?.stats || item;

      const ordersMap = {};
      events.forEach((evt) => {
        if (!evt.orderId) return;
        if (!ordersMap[evt.orderId]) {
          ordersMap[evt.orderId] = {
            orderId: evt.orderId,
            orderShortId: evt.orderShortId || "",
            userId: evt.userId || "",
            customerName: evt.customerName || "",
            customerPhone: evt.customerPhone || "",
            isGuest: evt.isGuest || false,
            orderAmount: Number(evt.orderAmount) || 0,
            orderSize: Number(evt.orderSize) || 0,
            createdAt: evt.createdAt,
            status: "pending",
          };
        }

        const ord = ordersMap[evt.orderId];
        if (evt.customerName && !ord.customerName) ord.customerName = evt.customerName;
        if (evt.customerPhone && !ord.customerPhone) ord.customerPhone = evt.customerPhone;
        if (evt.isGuest !== undefined) ord.isGuest = evt.isGuest;
        if (evt.orderShortId && !ord.orderShortId) ord.orderShortId = evt.orderShortId;
        if (evt.orderAmount && !ord.orderAmount) ord.orderAmount = Number(evt.orderAmount) || 0;
        if (evt.orderSize && !ord.orderSize) ord.orderSize = Number(evt.orderSize) || 0;
        if (evt.createdAt && (!ord.createdAt || evt.createdAt < ord.createdAt)) {
          ord.createdAt = evt.createdAt;
        }

        if (evt.type === "order_approved") {
          ord.status = "approved";
        } else if (evt.type === "order_cancelled") {
          ord.status = "cancelled";
        }
      });

      const ordersList = Object.values(ordersMap).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      const filename = exportTrackingLinkToXLSX({
        linkInfo: item,
        code: item.code,
        selectedRound: configs?.roundDate,
        displayStats: stats,
        ordersList,
        events,
      });

      toast({
        className: "bg-[rgb(59,176,101)]",
        title: "ดาวน์โหลดรายงาน Excel สำเร็จ",
        description: filename,
      });
    } catch (error) {
      console.error("Export error:", error);
      toast({
        className: "bg-[#c15b78]",
        title: "ดาวน์โหลดรายงานไม่สำเร็จ",
        description: error.message,
      });
    } finally {
      setDownloadingCode(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      return toast({
        className: "bg-[#c15b78]",
        title: "กรอกข้อมูลไม่ครบถ้วน",
        description: "กรุณาระบุชื่อและรหัส Tracking Code",
      });
    }

    setLoading(true);
    let result;
    if (editingItem) {
      result = await callApi("trackingLinks-editTrackingLink", {
        trackLinkId: editingItem.id,
        ...formData,
      });
    } else {
      result = await callApi("trackingLinks-createTrackingLink", formData);
    }

    if (result && result.status) {
      toast({
        className: "bg-[rgb(59,176,101)]",
        title: editingItem ? "อัปเดต Tracking Link สำเร็จ" : "สร้าง Tracking Link สำเร็จ",
      });
      setShowModal(false);
      fetchTrackingLinks();
    } else {
      toast({
        className: "bg-[#c15b78]",
        title: "เกิดข้อผิดพลาด",
        description: result?.message || "ไม่สามารถทำรายการได้",
      });
    }
    setLoading(false);
  };

  const handleToggleStatus = async (item) => {
    const newStatus = item.status === "active" ? "inactive" : "active";
    if (!window.confirm(`ต้องการเปลี่ยนสถานะของ ${item.name} เป็น ${newStatus} ใช่หรือไม่?`)) return;

    setLoading(true);
    const result = await callApi("trackingLinks-editTrackingLink", {
      trackLinkId: item.id,
      status: newStatus,
    });

    if (result && result.status) {
      toast({
        className: "bg-[rgb(59,176,101)]",
        title: "อัปเดตสถานะสำเร็จ",
      });
      fetchTrackingLinks();
    } else {
      toast({
        className: "bg-[#c15b78]",
        title: "อัปเดตไม่สำเร็จ",
        description: result?.message,
      });
    }
    setLoading(false);
  };

  const getFullUrl = (code) => {
    return getCustomerUrl(`/t/${code}`);
  };

  const copyLink = (code) => {
    const link = getFullUrl(code);
    navigator.clipboard.writeText(link);
    toast({
      className: "bg-[rgb(59,176,101)]",
      title: "คัดลอกลิงก์สำเร็จ",
      description: link,
    });
  };

  return (
    <div className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto pb-10">
      <Header
        header="Tracking Links"
        subHeader="จัดการลิงก์ติดตามยอดและกิจกรรมแคมเปญ / โฆษณา"
        icon={<FiLink className="text-blue text-2xl" />}
      />

      <div className="flex justify-between items-center my-4">
        <button
          onClick={handleExportAllXLSX}
          disabled={!trackingLinks || trackingLinks.length === 0}
          className="py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-md flex items-center gap-2 shadow-sm transition disabled:opacity-50"
        >
          <FaFileExcel className="text-base" /> ดาวน์โหลดรายงานภาพรวมทั้งหมด (.xlsx)
        </button>

        <button
          onClick={handleOpenCreateModal}
          className="py-2.5 px-4 bg-gradient-to-r from-[#002C7B] to-[#2065C4] text-white text-sm font-bold rounded-md hover:opacity-90 flex items-center gap-2 transition"
        >
          <FaPlus /> สร้าง Tracking Link ใหม่
        </button>
      </div>

      {/* Modal / Card Form */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex justify-center items-center p-4">
          <div className="flex flex-col justify-center p-6 shadow-md bg-white rounded-md w-full max-w-[500px] gap-3 box-border">
            <h3 className="text-xl font-bold text-navy text-center">
              {editingItem ? "แก้ไข Tracking Link" : "สร้าง Tracking Link ใหม่"}
            </h3>

            <form onSubmit={handleSubmit} className="w-full flex flex-col gap-3">
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1">
                  ชื่อแคมเปญ / ลิงก์ติดตาม <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="เช่น Facebook Ads Campaign A, TikTok Influencer B"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={inputCls}
                  required
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1">
                  Tracking Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="เช่น CAMPAIGN01"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value.toUpperCase() })
                  }
                  className={inputCls}
                  required
                />
                <p className="text-xs text-gray-400 mt-1">
                  ลิงก์จะเป็น: /t/{formData.code || "CODE"}
                </p>
              </div>

              {/* <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1">
                  ชื่อ
                </label>
                <input
                  type="text"
                  placeholder="ชื่อแคมเปญ"
                  value={formData.contact}
                  onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                  className={inputCls}
                />
              </div> */}

              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1">
                  หมายเหตุ
                </label>
                <textarea
                  placeholder="รายละเอียดเพิ่มเติม..."
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  className={`${inputCls} h-20 resize-none`}
                />
              </div>

              <div className="flex gap-2 justify-end mt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="text-sm font-semibold text-gray-500 hover:text-gray-700 px-4 py-2"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="py-2.5 px-4 bg-gradient-to-r from-[#002C7B] to-[#2065C4] text-white text-sm font-bold rounded-md hover:opacity-90"
                >
                  บันทึก
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-md shadow-md overflow-hidden">
        {loading && !trackingLinks ? (
          <div className="p-10 text-center">
            <Spinner />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>แคมเปญ / ลิงก์ติดตาม</TableHead>
                <TableHead className="text-center">Code / ลิงก์</TableHead>
                <TableHead className="text-center">Visits</TableHead>
                <TableHead className="text-center">สมัคร</TableHead>
                <TableHead className="text-center">สร้างคำสั่ง</TableHead>
                <TableHead className="text-center">อนุมัติ</TableHead>
                <TableHead className="text-center">ยกเลิก</TableHead>
                <TableHead className="text-right">ยอดขาย (บาท)</TableHead>
                {/* <TableHead className="text-center">Conversion</TableHead> */}
                <TableHead className="text-center">สถานะ</TableHead>
                <TableHead className="text-center">จัดการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trackingLinks && trackingLinks.length > 0 ? (
                trackingLinks.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div>
                        <p className="font-bold text-navy text-sm">{item.name}</p>
                        {item.contact && (
                          <p className="text-xs text-gray-400">{item.contact}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-center justify-between gap-1.5 bg-gray-50 border border-gray-200 px-2 py-1 rounded w-full max-w-[240px]">
                          <span className="font-mono text-xs font-semibold text-navy truncate" title={getFullUrl(item.code)}>
                            {getFullUrl(item.code)}
                          </span>
                          <button
                            onClick={() => copyLink(item.code)}
                            title="คัดลอกลิงก์"
                            className="p-1 text-gray-500 hover:text-navy hover:bg-gray-200 rounded text-xs flex-shrink-0 transition"
                          >
                            <FaCopy />
                          </button>
                        </div>
                        {/* <span className="text-[11px] font-mono text-gray-400">
                          Code: <strong className="text-gray-700">{item.code}</strong>
                        </span> */}
                      </div>
                    </TableCell>
                    <TableCell className="text-center font-bold">
                      {(item.visits || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-center font-bold text-blue-600">
                      {(item.registers || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-center font-bold text-green-600">
                      {(item.orderSuccesses || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-center font-bold text-emerald-600">
                      {(item.orderApproved || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-center font-bold text-red-500">
                      {(item.orderCancelled || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-bold text-green-700">
                      ฿{(item.totalRevenue || 0).toLocaleString()}
                    </TableCell>
                    {/* <TableCell className="text-center font-bold">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          Number(item.conversionRate) > 5
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {item.conversionRate}%
                      </span>
                    </TableCell> */}
                    <TableCell className="text-center">
                      <button
                        onClick={() => handleToggleStatus(item)}
                        className="text-xl"
                        title="คลิกเพื่อเปลี่ยนสถานะ"
                      >
                        {item.status === "active" ? (
                          <FaToggleOn className="text-green-500" />
                        ) : (
                          <FaToggleOff className="text-gray-400" />
                        )}
                      </button>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center items-center gap-2">
                        <button
                          onClick={() => handleDownloadXLSX(item)}
                          disabled={downloadingCode === item.code}
                          className="text-xs flex items-center gap-1 p-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded disabled:opacity-50 transition"
                          title="ดาวน์โหลด Excel (.xlsx)"
                        >
                          {downloadingCode === item.code ? (
                            <Spinner className="w-3 h-3" />
                          ) : (
                            <FaFileExcel className="text-xs" />
                          )}
                          Excel
                        </button>
                        <button
                          onClick={() => navigate(`/tracking-links/detail?code=${item.code}`)}
                          className="text-xs flex items-center gap-1 p-1.5 bg-navy text-white font-semibold rounded hover:opacity-90"
                          title="ดูรายละเอียด Funnel"
                        >
                          <IoStatsChart /> สถิติ
                        </button>
                        <button
                          onClick={() => handleOpenEditModal(item)}
                          className="text-xs p-1.5 bg-yellow-400 font-semibold rounded hover:opacity-90"
                        >
                          แก้ไข
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-gray-400">
                    ยังไม่มีข้อมูล Tracking Links
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};
